import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Field, reduxForm } from 'redux-form';
import { useTranslation } from 'react-i18next';
import { Badge, Form, Row, Col, ButtonGroup, Button, Card, CardBody, CardTitle, CardText, CardFooter } from 'reactstrap';
import { Loader, PageTitle, LangsField, PlacesField, SelectField, ToggleField } from '../../components';
import { loadTktInstanceQrImages, loadPlaces } from '../../redux';
import i18n from '../../locales/i18n';
import moment from 'moment';
import _ from 'lodash';

import './ConfigurationQrCodes.css';

const APP_MODE_TICKET_POS     = 'POS';
const APP_MODE_ENTRY_CHECK    = 'ENTRY_CHECK';
const APP_MODE_TERMINAL       = 'TERMINAL';
const APP_MODE_VENUE_MGT      = 'VENUE_MGT';
const APP_MODE_USHER          = 'USHER';
const APP_MODE_WALLET_CASHIER = 'WALLET_CASHIER';
const APP_MODE_INVENTORY      = 'INVENTORY';

const CAMERA_BACK  = 'back';
const CAMERA_FRONT = 'front';

const SCAN_MODE_SCREENING = 'screening';
const SCAN_MODE_BOOKING   = 'first_booking';

const SCAN_DIR_IN  = 'in';
const SCAN_DIR_OUT = 'out';

const UNSCAN_AT_CUSTOM            = 'custom';
const UNSCAN_AT_SCREENING_STOP_AT = 'screening_stop_at';

const ConfigurationQrCodes = ({ handleSubmit, submitting }) => {
    const dispatch    = useDispatch();
    const { t, i18n } = useTranslation();

    const [params, setParams] = useState({
        'mode': APP_MODE_TICKET_POS,
        'lang': i18n.language,
        'venues': [],
        'camera': CAMERA_BACK,
        'scan_dir': SCAN_DIR_IN,
        'scan_mode': SCAN_MODE_SCREENING,
        'standalone_scanner': false,
        'scan_url': null,
        'scan_only_one_booking': false,
        'unscan_at_mode': null,
        'unscan_at': null
    });

    const onSubmit = values => { };

    const sanitizeParams = newParams => {
        if (!newParams.unscan_at_mode)
            newParams.unscan_at = null;
        else if (newParams.unscan_at_mode === UNSCAN_AT_SCREENING_STOP_AT)
            newParams.unscan_at = newParams.unscan_at_mode;
        setParams(newParams);
    };

    const { isMobile, isTablet } = useSelector(state => state.ui);
    const { places }             = useSelector(state => state.places);
    const { qrs, isLoading, loadingErrorMessage } = useSelector(state => state.tktInstances);

    useEffect(() => {
        dispatch(loadTktInstanceQrImages(params));
        dispatch(loadPlaces());
    }, [ params ]);

    const title = <span>{t("app.configuration_qr_codes")}</span>;

    const modeSelector = React.useMemo(() => {
        const modes = [
            APP_MODE_TICKET_POS,
            APP_MODE_ENTRY_CHECK,
            APP_MODE_TERMINAL,
            APP_MODE_VENUE_MGT,
            APP_MODE_USHER,
            APP_MODE_WALLET_CASHIER,
            APP_MODE_INVENTORY
        ];

        return (
            <ButtonGroup className="w-100" vertical={isMobile || isTablet}>
                { modes.map(mode => (
                    <Button
                        key={ mode }
                        color={ params.mode === mode ? 'primary' : 'dark'}
                        onClick={ () => sanitizeParams({ ...params, mode })}
                    >
                        <i className="fa fa-mobile" />&nbsp;
                        { t(`configuration_qr_codes.mode.${mode}`) }
                    </Button>
                ))}
            </ButtonGroup>
        );
    }, [ params?.mode, isMobile, isTablet, i18n.language ]);

    const qrsRender = React.useMemo(() => {
        if (isLoading)
            return <Loader />;

        if (!qrs?.length)
            return null;

        return (
            <div className="my-2 text-center">
                <Row>
                    {qrs.map((qr, index) => (
                        <Col className="mb-4" key={index} xs={12} lg={qrs.length > 1 ? 6 : 12} xl={qrs.length > 1 ? 4 : 12}>
                            <Card>
                                <CardBody>
                                    <CardText tag="div">
                                        <img src={qr.image} />
                                    </CardText>
                                </CardBody>
                                <CardFooter>
                                    <h3>
                                        { qr.label }
                                    </h3>
                                    {qr.validity && (
                                        <h5 className="noPrint">
                                            <Badge color={moment(qr.validity).isBefore(moment().add(1, 'month')) ? 'danger' : 'success'}>
                                                <i className="fa fa-hourglass-end" />&nbsp;
                                                { moment(qr.validity).format('LLL') }
                                            </Badge>
                                        </h5>
                                    )}
                                </CardFooter>
                            </Card>
                        </Col>
                    ))}
                </Row>
            </div>
        );
    }, [ qrs, i18n.language, isLoading ]);

    const form = React.useMemo(() => {
        if (!params?.mode)
            return null;

        const config = ({
            [APP_MODE_TICKET_POS]: ['lang', 'camera', 'venues'],
            [APP_MODE_ENTRY_CHECK]: [
                'lang', 'camera', 'venues', 'scan_mode',
                'scan_only_one_booking', 'standalone_scanner',
                'scan_url', 'scan_dir', 'unscan_at'
            ],
            [APP_MODE_TERMINAL]: ['lang'],
            [APP_MODE_VENUE_MGT]: ['lang', 'venues'],
            [APP_MODE_USHER]: ['lang', 'venues'],
            [APP_MODE_WALLET_CASHIER]: [],
            [APP_MODE_INVENTORY]: ['lang']
        })[params.mode];

        if (!config?.length)
            return null;

        const wrap = content => <Col xs={12} lg={4}>{ content }</Col>;

        const cameraOptions = [CAMERA_BACK, CAMERA_FRONT].map(cam => ({
            key: cam,
            text: t(`configuration_qr_codes.camera_${cam}`),
            value: cam,
        }));

        const scanModeOptions = [SCAN_MODE_SCREENING, SCAN_MODE_BOOKING].map(mode => ({
            key: mode,
            text: t(`configuration_qr_codes.scan_mode_${mode}`),
            value: mode,
        }));

        const scanDirOptions = [SCAN_DIR_IN, SCAN_DIR_OUT].map(dir => ({
            key: dir,
            text: t(`configuration_qr_codes.scan_dir_${dir}`),
            value: dir,
        }));

        const unscanOptions = [UNSCAN_AT_SCREENING_STOP_AT, UNSCAN_AT_CUSTOM].map(at => ({
            key: at,
            text: t(`configuration_qr_codes.unscan_at_${at}`),
            value: at,
        }));

        return (
            <Form onSubmit={handleSubmit(onSubmit)}>
                <Card>
                    <CardBody>
                        <Row>
                            {config.includes('lang') && wrap(
                                <div className="form-group">
                                    <label for="">{t('configuration_qr_codes.lang')}</label>
                                    <LangsField name="lang" onChange={lang => sanitizeParams({ ...params, lang })} />
                                </div>
                            )}

                            {config.includes('camera') && wrap(
                                <div className="form-group">
                                    <label for="">{t('configuration_qr_codes.camera')}</label>
                                    <SelectField name="camera" clearable options={ cameraOptions } onSelectChange={ camera => sanitizeParams({ ...params, camera }) } />
                                </div>
                            )}

                            {config.includes('venues') && (places?.length > 0) && wrap(
                                <div className="form-group">
                                    <label for="">{t('configuration_qr_codes.venues')}</label>
                                    <PlacesField places={places} value={params.venues} onChange={ venues => sanitizeParams({ ...params, venues }) } />
                                </div>
                            )}

                            {config.includes('scan_mode') && wrap(
                                <div className="form-group">
                                    <label for="">{t('configuration_qr_codes.scan_mode_first_booking')}</label>
                                    <ToggleField name="scan_mode" onSelectChange={ scan_mode => sanitizeParams({ ...params, scan_mode: !!scan_mode ? SCAN_MODE_BOOKING : SCAN_MODE_SCREENING }) } />
                                </div>
                            )}

                            {config.includes('scan_only_one_booking') && wrap(
                                <div className="form-group">
                                    <label for="">{t('configuration_qr_codes.scan_only_one_booking')}</label>
                                    <ToggleField name="scan_only_one_booking" onSelectChange={ scan_only_one_booking => sanitizeParams({ ...params, scan_only_one_booking }) } />
                                </div>
                            )}

                            {config.includes('standalone_scanner') && wrap(
                                <div className="form-group">
                                    <label for="">{t('configuration_qr_codes.standalone_scanner')}</label>
                                    <ToggleField name="standalone_scanner" onSelectChange={ standalone_scanner => sanitizeParams({ ...params, standalone_scanner }) } />
                                </div>
                            )}

                            {config.includes('scan_dir') && wrap(
                                <div className="form-group">
                                    <label for="">{t('configuration_qr_codes.scan_dir')}</label>
                                    <SelectField name="scan_dir" clearable options={ scanDirOptions } onSelectChange={ scan_dir => sanitizeParams({ ...params, scan_dir }) } />
                                </div>
                            )}

                            {config.includes('unscan_at') && wrap(
                                <div className="form-group">
                                    <label for="">{t('configuration_qr_codes.unscan_at')}</label>
                                    <SelectField name="unscan_at_mode" clearable options={ unscanOptions } onSelectChange={ unscan_at_mode => sanitizeParams({ ...params, unscan_at_mode }) } />
                                    {params.unscan_at_mode === UNSCAN_AT_CUSTOM && (
                                        <Field className="form-control" component="input" type="text" name="unscan_at" onChange={ e => sanitizeParams({ ...params, unscan_at: e.target.value }) } placeholder="PT30M" />
                                    )}
                                </div>
                            )}

                            {config.includes('scan_url') && params.standalone_scanner && wrap(
                                <div className="form-group">
                                    <label for="">{t('configuration_qr_codes.scan_url')}</label>
                                    <Field className="form-control" component="input" type="text" name="scan_url" onChange={ scan_url => sanitizeParams({ ...params, scan_url }) } />
                                </div>
                            )}
                        </Row>
                    </CardBody>
                </Card>
            </Form>
        );
    }, [ params?.mode, i18n.language ]);

    const actions = null;

    return (
        <div className="ConfigurationQrCodes">
            <PageTitle icon="qrcode" title={title} actions={actions} />
            <div className="mt-2 noPrint">
                { modeSelector }
            </div>
            <div className="mt-4 noPrint">
                { form }
            </div>
            <div className="mt-4">
                { qrsRender }
            </div>
            {loadingErrorMessage && (
                <div className="alert alert-danger">{loadingErrorMessage}</div>
            )}
        </div>
    );
}

const ConfigurationQrCodesForm = reduxForm({
    form: 'configurationQrCodesForm',
    enableReinitialize: true,
    initialValues: {
        'mode': APP_MODE_TICKET_POS,
        'lang': i18n.language,
        'venues': [],
        'camera': CAMERA_BACK,
        'scan_dir': SCAN_DIR_IN,
        'scan_mode': false,
        'standalone_scanner': false,
        'scan_url': null,
        'scan_only_one_booking': false,
        'unscan_at': null
    }
})(ConfigurationQrCodes);

export default ConfigurationQrCodesForm;
